import React, { useRef, useState } from 'react';
import { ImageRightsAPI } from '../../api/ImageRightsAPI';
import { ALL_SPECIAL_TYPES } from '../../constants/EventConstants';
import { useArmedClick } from './pictureParts';

export const typeLabel = (specialType) =>
  ALL_SPECIAL_TYPES.find((type) => type.value === specialType)?.label ?? specialType;

const kilobytes = (bytes) => `${Math.max(1, Math.round((bytes || 0) / 1024))} KB`;

const usesLabel = (uses) => {
  if (!uses) return 'Nicht in Verwendung';
  return `Bei ${uses} ${uses === 1 ? 'kommendem Event' : 'kommenden Events'}`;
};

function PictureCard({ picture, busy, onRemove }) {
  const [armed, remove] = useArmedClick(() => onRemove(picture));
  return (
    <article className={'isy-card' + (busy ? ' isy-card--busy' : '')}>
      <a className="isy-card-image" href={picture.url} target="_blank" rel="noreferrer" title="In voller Größe öffnen">
        <img src={picture.url} alt="" loading="lazy" />
      </a>
      <div className="isy-card-body">
        <strong className="isy-card-name">{picture.name}</strong>
        <span className="isy-card-meta">{picture.width} × {picture.height} · {kilobytes(picture.sizeBytes)}</span>
        <span className={'isy-card-uses' + (picture.uses ? ' is-used' : '')}>{usesLabel(picture.uses)}</span>
        <button type="button" className={'pp-btn ' + (armed ? 'pp-btn--armed' : 'pp-btn--danger')}
          onClick={remove} disabled={busy}
          title="Bild löschen. Events damit zeigen danach wieder die Schick-Grafik. Lässt sich nicht rückgängig machen.">
          {armed
            ? (picture.uses ? `Wirklich? ${picture.uses} ${picture.uses === 1 ? 'Event zeigt' : 'Events zeigen'} dann die Schick-Grafik` : 'Wirklich entfernen?')
            : 'Entfernen'}
        </button>
      </div>
    </article>
  );
}

/**
 * Our AI symbol pictures by kind of event: upload new ones (cut to 4:5 and compressed by the
 * backend) and remove ones we no longer want. Events showing a removed picture fall back to
 * the Schick artwork.
 */
export default function SymbolPictureManager({ data, onChanged }) {
  const [specialType, setSpecialType] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(() => new Set());
  const fileInput = useRef(null);

  const upload = async (event) => {
    event.preventDefault();
    if (!specialType || files.length === 0) return;
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const answer = await ImageRightsAPI.uploadSymbolImages(specialType, files);
      setResult({ ...answer, specialType });
      setFiles([]);
      if (fileInput.current) fileInput.current.value = '';
      if (answer.added?.length) onChanged?.();
    } catch (err) {
      console.error('Fehler beim Hochladen:', err);
      setError(err?.response?.data?.message || 'Konnte die Bilder nicht hochladen.');
    } finally {
      setUploading(false);
    }
  };

  const removePicture = async (picture) => {
    setBusy((prev) => new Set(prev).add(picture.name));
    setError(null);
    try {
      await ImageRightsAPI.removeSymbolImage(picture.name);
      onChanged?.();
    } catch (err) {
      console.error('Fehler beim Entfernen:', err);
      setError(`Konnte ${picture.name} nicht entfernen.`);
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(picture.name);
        return next;
      });
    }
  };

  const types = data?.types ?? [];

  return (
    <div className="isy-manage">
      <form className="isy-upload" onSubmit={upload} aria-label="KI-Symbolbilder hochladen">
        <div className="isy-upload-fields">
          <select className="isy-select" value={specialType} onChange={(event) => setSpecialType(event.target.value)}
            aria-label="Kategorie" disabled={uploading}>
            <option value="">Kategorie wählen …</option>
            {ALL_SPECIAL_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
          </select>
          <input ref={fileInput} className="isy-file" type="file" accept="image/jpeg,image/png" multiple
            aria-label="Bilder" disabled={uploading}
            onChange={(event) => setFiles([...(event.target.files || [])])} />
          <button className="btn-add btn-add--green" type="submit" disabled={uploading || !specialType || files.length === 0}>
            {uploading ? 'Lädt hoch …' : files.length > 1 ? `${files.length} Bilder hochladen` : 'Hochladen'}
          </button>
        </div>
        <p className="isy-upload-hint">
          JPG oder PNG im Hochformat. Jedes Bild wird mittig auf 4:5 zugeschnitten, auf höchstens 1080 × 1350 verkleinert,
          komprimiert und als KI-Bild markiert. Vorher prüfen: keine echten Personen, Orte, Marken oder Preise im Bild.
        </p>
        {result && (
          <div className="isy-result" role="status">
            {result.added.length > 0 && (
              <p className="isy-result-ok">
                {result.added.length === 1 ? '1 Bild' : `${result.added.length} Bilder`} für {typeLabel(result.specialType)} hinzugefügt
                {' '}({result.added.map((picture) => picture.name).join(', ')}).
              </p>
            )}
            {result.rejected.map((each) => (
              <p key={each.file} className="isy-result-bad"><strong>{each.file}:</strong> {each.reason}</p>
            ))}
          </div>
        )}
      </form>

      {error && <p className="pp-error">{error}</p>}

      {types.length === 0 ? (
        <div className="pp-empty">Noch keine KI-Symbolbilder. Lade oben die ersten hoch.</div>
      ) : types.map((type) => (
        <section key={type.specialType} className="isy-type" aria-label={typeLabel(type.specialType)}>
          <h3 className="isy-type-title">
            {typeLabel(type.specialType)}
            <span>{type.pictures.length} {type.pictures.length === 1 ? 'Bild' : 'Bilder'}</span>
          </h3>
          <div className="isy-cards">
            {type.pictures.map((picture) => (
              <PictureCard key={picture.name} picture={picture} busy={busy.has(picture.name)} onRemove={removePicture} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
